import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, User, LogOut, Mail, MessageCircle, Settings, Coins, CalendarDays, GraduationCap, Heart, Gift } from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { CurriculumModal } from "./CurriculumModal";
import { MembershipModal } from "./MembershipModal";
import { AuthModal } from "../AuthModal";
import { useAuth } from "../../context/AuthContext";
import { NotificationDropdown } from "../NotificationDropdown";
import { ReferralPopover } from "../ReferralBanner";
import { SubscriptionWarning } from "../SubscriptionWarning";
import MembershipGuardModal, { canViewMembers } from "../MembershipGuardModal";
import ReferralModal from "../ReferralModal";

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, token } = useAuth();
  // 관리자 판별 — role/is_admin 플래그/관리자 이메일/membership_type 모두 인식 (견고성)
  const isAdminUser = !!(
    user && (
      user.is_admin ||
      user.role === "admin" ||
      user.membership_type === "admin" ||
      (user.email || "").toLowerCase() === "admin@newcoupling.com"
    )
  );
  const [isScrolled, setIsScrolled] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCommunityOpen, setIsMobileCommunityOpen] = useState(false);
  const [isMobileMatchingOpen, setIsMobileMatchingOpen] = useState(false);
  const [isMobileCoachingOpen, setIsMobileCoachingOpen] = useState(false);
  const [isMobileCounselingOpen, setIsMobileCounselingOpen] = useState(false);
  const [isMobileEventOpen, setIsMobileEventOpen] = useState(false);
  const [isMobileMembershipOpen, setIsMobileMembershipOpen] = useState(false);
  const [mobileCoupleClubOpen, setMobileCoupleClubOpen] = useState(false);
  const [isCurriculumModalOpen, setIsCurriculumModalOpen] = useState(false);
  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [membersGuard, setMembersGuard] = useState(null); // null | "auth" | "upgrade"
  const [showReferral, setShowReferral] = useState(false);

  // 회원목록 진입 가드: 정회원만 통과
  const goToMembers = (href = "/members") => {
    if (!isAuthenticated) {
      setMembersGuard("auth");
      setIsMobileMenuOpen(false);
      return;
    }
    if (!canViewMembers(user)) {
      setMembersGuard("upgrade");
      setIsMobileMenuOpen(false);
      return;
    }
    navigate(href);
    setIsMobileMenuOpen(false);
    document.body.click(); // 데스크탑 드롭다운 닫기
  };

  // Check if we're on landing page
  const isLandingPage = location.pathname === "/";

  // Only track scroll on landing page for transparent->solid transition
  useEffect(() => {
    if (!isLandingPage) {
      setIsScrolled(true); // Always show solid bg on non-landing pages
      return;
    }
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    handleScroll(); // Check initial scroll position
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isLandingPage]);

  // Listen for custom event to open auth modal
  useEffect(() => {
    const handleOpenAuthModal = (event) => {
      setIsAuthModalOpen(true);
      setAuthModalInitialMode(event.detail?.mode || 'login');
    };
    
    window.addEventListener('openAuthModal', handleOpenAuthModal);
    return () => window.removeEventListener('openAuthModal', handleOpenAuthModal);
  }, []);

  const [authModalInitialMode, setAuthModalInitialMode] = useState('login');

  // Route 변경 시 모든 드롭다운 닫기
  useEffect(() => {
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  }, [location.pathname, location.search]);

  // 메시지 unread 카운트 (30초 간격 폴링)
  useEffect(() => {
    if (!isAuthenticated || !token) { setUnreadCount(0); return; }
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/messages/unread-count`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setUnreadCount(d.count || 0);
      } catch { /* ignore */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [isAuthenticated, token, location.pathname]);

  // 새 SNS 카테고리 (image 2 기준) — /community 페이지의 좌측 사이드바와 동일
  const communityMenuItems = [
    { label: "연애 & 인간관계", categoryId: "relationships" },
    { label: "여행", categoryId: "travel" },
    { label: "건강", categoryId: "health" },
    { label: "취미", categoryId: "hobby" },
    { label: "노후 & 인생2막", categoryId: "retirement" },
    { label: "자유게시판", categoryId: "free" },
    { label: "오늘의 하루", categoryId: "today_moment" },
    { label: "익명 고민상담", categoryId: "anonymous_advice" },
  ];

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // 리즈커플즈 메뉴
  const coupleClubMenuItems = {
    coupleBoard: {
      title: "커플 게시판",
      href: "/couple-board",
      items: [
        { label: "성공스토리", href: "/couple-board?category=success" },
        { label: "데이트 후기", href: "/couple-board?category=date-review" },
        { label: "이벤트", href: "/couple-board?category=event" },
        { label: "커플 채팅방", href: "/couple-chat" },
      ],
    },
  };

  // 소그룹 채팅 메뉴
  const groupChatMenuItems = [
    { label: "40대 채팅방", ageGroup: "40s" },
    { label: "50대 채팅방", ageGroup: "50s" },
    { label: "60대 채팅방", ageGroup: "60s" },
  ];

  const matchingMenuItems = [
    // { label: "AI 스마트 매칭", href: "/smart-match", isRoute: true },
    { label: "데이팅 앱", href: "/dating-app", isRoute: true },
    { label: "글로벌 줌미팅", href: "/global-zoom", isRoute: true },
    { label: "3:3 줌데이트", href: "/zoom-date", isRoute: true },
    // { label: "6:6 소그룹 오프미팅", href: "/group-meeting", isRoute: true },
    { label: "연인만들기 프로젝트", href: "/love-project", isRoute: true },
    { label: "연애정보회사", href: "/dating-agency", isRoute: true },
  ];

  const coachingMenuItems = {
    imageConsulting: {
      title: "이미지 컨설팅",
      items: [
        { label: "패션", href: "/image-consulting?category=fashion", isRoute: true },
        { label: "헤어", href: "/image-consulting?category=hair", isRoute: true },
        { label: "메이크업", href: "/image-consulting?category=makeup", isRoute: true },
        { label: "시술&성형", href: "/image-consulting?category=procedure", isRoute: true },
        { label: "프로필 사진", href: "/image-consulting?category=profile", isRoute: true },
      ],
    },
    mannerClass: {
      title: "매너클라스",
      items: [
        { label: "첫인상 매너", href: "/manner-class?category=first_impression", isRoute: true },
        { label: "데이트매너", href: "/manner-class?category=date", isRoute: true },
        { label: "식사매너", href: "/manner-class?category=dining", isRoute: true },
        { label: "대화매너", href: "/manner-class?category=conversation", isRoute: true },
        { label: "감정매너", href: "/manner-class?category=emotion", isRoute: true },
        { label: "연락매너", href: "/manner-class?category=contact", isRoute: true },
        { label: "거절매너", href: "/manner-class?category=rejection", isRoute: true },
      ],
    },
    loveAcademy: {
      title: "연애사관학교",
      items: [
        { label: "자존감학과", href: "/curriculum/1", isRoute: true },
        { label: "소통기술학과", href: "/curriculum/2", isRoute: true },
        { label: "여성학과", href: "/curriculum/3", isRoute: true },
        { label: "남성학과", href: "/curriculum/4", isRoute: true },
        { label: "연애학과", href: "/curriculum/5", isRoute: true },
        { label: "매력학과", href: "/curriculum/6", isRoute: true },
        { label: "사랑학과", href: "/curriculum/7", isRoute: true },
        { label: "갈등학과", href: "/curriculum/8", isRoute: true },
        { label: "이혼 치유학과", href: "/curriculum/9", isRoute: true },
        { label: "사별 회복학과", href: "/curriculum/10", isRoute: true },
        { label: "결혼학과", href: "/curriculum/11", isRoute: true },
        { label: "중년성학과", href: "/curriculum/12", isRoute: true },
      ],
    },
  };

  const counselingMenuItems = [
    { label: "결혼과 재혼", href: "/counseling", serviceId: "marriage" },
    { label: "연애와 사랑", href: "/counseling", serviceId: "love" },
    { label: "이별과 이혼", href: "/counseling", serviceId: "breakup" },
    { label: "재회상담", href: "/counseling", serviceId: "reunion" },
    { label: "커플 테라피", href: "/counseling", serviceId: "couple" },
    { label: "기타상담", href: "/counseling", serviceId: "other" },
  ];

  const eventMenuItems = {
    offlineMeeting: {
      title: "전체보기",
      titleHref: "/events",
      items: [
        { label: "4050", href: "/events?category=offline&sub=4050", isRoute: true },
        { label: "5060", href: "/events?category=offline&sub=5060", isRoute: true },
      ],
    },
    matchingDinner: {
      title: "매칭&디너",
      titleHref: "/events?category=matching",
      items: [],
    },
    smallGroup: {
      title: "소모임",
      items: [
        { label: "취미별", href: "/events?category=small&sub=hobby", isRoute: true },
        { label: "지역별", href: "/events?category=small&sub=region", isRoute: true },
        { label: "연령별", href: "/events?category=small&sub=age", isRoute: true },
      ],
    },
    party: {
      title: "파티",
      items: [
        { label: "발렌타인 데이", href: "/events?category=party&sub=valentine", isRoute: true },
        { label: "연말 송년회", href: "/events?category=party&sub=yearend", isRoute: true },
      ],
    },
    camp: {
      title: "캠프",
      items: [
        { label: "힐링캠프", href: "/events?category=camp&sub=healing", isRoute: true },
        { label: "1박 캠프", href: "/events?category=camp&sub=overnight", isRoute: true },
      ],
    },
  };

  const REGION_ID_MAP_NAV = {
    KR: "korea", US: "usa", CA: "canada", AU: "australia", JP: "japan", CN: "china",
    GB: "uk", DE: "germany", FR: "france", SG: "singapore", NZ: "newzealand",
  };

  // 회원정보보기 드롭다운: 한국/미국/캐나다/호주 4개국만 노출 (요청사항)
  const [dynamicMemberItems] = useState([
    { label: "한국", href: "/members?region=korea", isRoute: true },
    { label: "미국", href: "/members?region=usa", isRoute: true },
    { label: "캐나다", href: "/members?region=canada", isRoute: true },
    { label: "호주", href: "/members?region=australia", isRoute: true },
  ]);

  const membershipMenuItems = {
    memberInfo: {
      title: "회원정보보기",
      items: dynamicMemberItems,
    },
    plans: {
      title: "가입표",
      items: [
        { label: "💳 가격 / 결제 (PayPal)", planId: "__pricing__" },
        { label: "일반회원", planId: "basic" },
        { label: "정회원", planId: "regular" },
        { label: "프리미엄회원", planId: "premium" },
        { label: "VIP 회원", planId: "vip" },
      ],
    },
  };

  const navLinks = [
    // Auth link is now handled separately
  ];

  const openAuthModal = () => {
    setIsAuthModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  const goToProfile = () => {
    navigate("/profile");
    setIsMobileMenuOpen(false);
  };

  const openCurriculumModal = () => {
    setIsCurriculumModalOpen(true);
    setIsMobileMenuOpen(false);
    setIsMobileCoachingOpen(false);
  };

  const openMembershipModal = () => {
    setIsMembershipModalOpen(true);
    setIsMobileMenuOpen(false);
    setIsMobileMembershipOpen(false);
  };

  const goToMembershipPage = (planId = null) => {
    if (planId === "__pricing__") {
      navigate('/pricing');
    } else if (planId) {
      navigate(`/membership?plan=${planId}`);
    } else {
      navigate('/membership');
    }
    setIsMobileMenuOpen(false);
    setIsMobileMembershipOpen(false);
  };

  const goToCommunity = (categoryId = null) => {
    if (categoryId) {
      navigate(`/community?cat=${categoryId}`);
    } else {
      navigate("/community");
    }
    setIsMobileMenuOpen(false);
    setIsMobileCommunityOpen(false);
  };

  const goToCommunityItem = (item) => {
    if (item.href) {
      navigate(item.href);
    } else if (item.categoryId) {
      navigate(`/community?cat=${item.categoryId}`);
    } else {
      navigate("/community");
    }
    setIsMobileMenuOpen(false);
    setIsMobileCommunityOpen(false);
  };

  const scrollToSection = (href) => {
    // Handle paths like "/#curriculum" - navigate to home page and scroll to section
    if (href.startsWith("/#")) {
      const sectionId = href.substring(1); // Get "#curriculum"
      const currentPath = window.location.pathname;
      
      if (currentPath === "/") {
        // Already on home page, just scroll
        const element = document.querySelector(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to home page with hash
        navigate(href);
      }
      setIsMobileMenuOpen(false);
      setIsMobileCommunityOpen(false);
      setIsMobileMatchingOpen(false);
      setIsMobileCoachingOpen(false);
      setIsMobileCounselingOpen(false);
      setIsMobileEventOpen(false);
      setIsMobileMembershipOpen(false);
      return;
    }
    
    if (href.startsWith("/")) {
      navigate(href);
      setIsMobileMenuOpen(false);
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
    setIsMobileCommunityOpen(false);
    setIsMobileMatchingOpen(false);
    setIsMobileCoachingOpen(false);
    setIsMobileCounselingOpen(false);
    setIsMobileEventOpen(false);
    setIsMobileMembershipOpen(false);
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      <SubscriptionWarning />
      <nav
        data-testid="navigation"
        className={`transition-all duration-300 ${
          isScrolled
            ? "bg-white shadow-sm"
            : "bg-transparent"
        }`}
      >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              localStorage.removeItem("nc_visited");
              navigate("/");
            }}
            className="flex items-center gap-2"
            data-testid="logo"
          >
            <span className={`flex items-start gap-2 transition-colors duration-300`}>
              <img
                src="/logo.png"
                alt="New Coupling"
                className={`h-7 w-auto transition duration-300 ${
                  isScrolled ? "" : "brightness-0 invert"
                }`}
              />
              <div className="flex flex-col leading-tight" style={{ width: 'fit-content' }}>
                <span className={`font-sans text-xl font-[720] ${
                  isScrolled
                    ? "bg-clip-text text-transparent bg-gradient-to-r from-burgundy-700 via-burgundy-500 to-rose-400"
                    : "text-white"
                }`}>New Coupling</span>
                <span 
                  className={`text-xs font-normal flex justify-between w-full ${isScrolled ? "text-stone-400" : "text-white/50"}`}
                ><span>글로벌</span><span>한인 중년</span><span>커뮤니티</span></span>
              </div>
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className={`hidden md:flex items-center gap-5 ${
            isScrolled ? "" : "text-white"
          }`}>
            {/* 커뮤니티 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-community-trigger"
              >
                커뮤니티
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[480px] bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-3"
                data-testid="community-dropdown-content"
              >
                <DropdownMenuLabel 
                  className="px-4 py-2 text-xs text-burgundy-500 font-semibold cursor-pointer hover:bg-burgundy-50 rounded-lg"
                  onClick={() => goToCommunity()}
                >
                  중년들의 결로 모이는 따뜻한 SNS 커뮤니티
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-stone-200" />
                <div className="grid grid-cols-2 gap-0.5">
                  {communityMenuItems.map((item, index) => (
                    <DropdownMenuItem
                      key={item.label}
                      onClick={() => goToCommunityItem(item)}
                      className="px-3 py-2 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                      data-testid={`community-item-${index}`}
                    >
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </div>
                <DropdownMenuSeparator className="bg-stone-200 my-2" />
                <DropdownMenuItem
                  onClick={() => navigate("/chat/group")}
                  className="px-4 py-2.5 text-sm text-burgundy-600 hover:text-burgundy-700 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2 font-semibold"
                  data-testid="group-chat-landing-link"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  소그룹 채팅방
                </DropdownMenuItem>
                {groupChatMenuItems.map((item, index) => (
                  <DropdownMenuItem
                    key={item.ageGroup}
                    onClick={() => {
                      if (!isAuthenticated) {
                        openAuthModal();
                        return;
                      }
                      navigate(`/chat/group/${item.ageGroup}`);
                    }}
                    className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2 pl-8"
                    data-testid={`group-chat-${item.ageGroup}`}
                  >
                    <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 매칭 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-matching-trigger"
              >
                매칭
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-56 bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-2"
                data-testid="matching-dropdown-content"
              >
                {matchingMenuItems.map((item, index) => (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                    className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                    data-testid={`matching-item-${index}`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 이벤트 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-event-trigger"
              >
                이벤트
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[480px] bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-3"
                data-testid="event-dropdown-content"
              >
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {/* 오프모임 → 전체보기 */}
                  <div>
                    <DropdownMenuLabel
                      onClick={() => navigate(eventMenuItems.offlineMeeting.titleHref || "/events")}
                      className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:text-burgundy-700"
                      data-testid="event-offline-title"
                    >
                      {eventMenuItems.offlineMeeting.title}
                    </DropdownMenuLabel>
                    {/* 매칭&디너 — 전체보기 바로 아래 */}
                    <DropdownMenuLabel
                      onClick={() => navigate(eventMenuItems.matchingDinner.titleHref || "/events")}
                      className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:text-burgundy-700"
                      data-testid="event-matching-title"
                    >
                      {eventMenuItems.matchingDinner.title}
                    </DropdownMenuLabel>
                    {eventMenuItems.offlineMeeting.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`event-offline-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {/* 소모임 */}
                  <div>
                    <DropdownMenuLabel className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline">
                      {eventMenuItems.smallGroup.title}
                    </DropdownMenuLabel>
                    {eventMenuItems.smallGroup.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`event-small-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {/* 파티 */}
                  <div>
                    <DropdownMenuLabel className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline">
                      {eventMenuItems.party.title}
                    </DropdownMenuLabel>
                    {eventMenuItems.party.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`event-party-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {/* 캠프 */}
                  <div>
                    <DropdownMenuLabel className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline">
                      {eventMenuItems.camp.title}
                    </DropdownMenuLabel>
                    {eventMenuItems.camp.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`event-camp-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 코칭&컨설팅 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-coaching-trigger"
              >
                코칭&컨설팅
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-[680px] bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-3"
                data-testid="coaching-dropdown-content"
              >
                <div className="grid grid-cols-3 gap-x-4">
                  {/* 이미지 컨설팅 */}
                  <div>
                    <DropdownMenuLabel 
                      className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:bg-burgundy-50 rounded-lg"
                      onClick={() => { navigate('/image-consulting'); document.body.click(); }}
                    >
                      {coachingMenuItems.imageConsulting.title}
                    </DropdownMenuLabel>
                    {coachingMenuItems.imageConsulting.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`coaching-image-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {/* 매너클라스 */}
                  <div>
                    <DropdownMenuLabel 
                      className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:bg-burgundy-50 rounded-lg"
                      onClick={() => { navigate('/manner-class'); document.body.click(); }}
                    >
                      {coachingMenuItems.mannerClass.title}
                    </DropdownMenuLabel>
                    {coachingMenuItems.mannerClass.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`coaching-manner-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>

                  {/* 연애사관학교 */}
                  <div>
                    <DropdownMenuLabel 
                      className="px-3 py-1.5 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:bg-burgundy-50 rounded-lg"
                      onClick={() => { navigate('/love-academy'); document.body.click(); }}
                    >
                      {coachingMenuItems.loveAcademy.title}
                    </DropdownMenuLabel>
                    {coachingMenuItems.loveAcademy.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => item.isRoute ? navigate(item.href) : scrollToSection(item.href)}
                        className="px-3 py-1.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`coaching-academy-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 리즈상담 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-counseling-trigger"
              >
                리즈상담
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-56 bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-2"
                data-testid="counseling-dropdown-content"
              >
                {counselingMenuItems.map((item, index) => (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => navigate(item.href)}
                    className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                    data-testid={`counseling-item-${index}`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 리즈커플즈 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-couple-club-trigger"
              >
                리즈커플즈
                <ChevronDown className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-64 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-stone-200 p-3 mt-2"
                data-testid="couple-club-dropdown-content"
              >
                {/* 커플 게시판 섹션 */}
                <div className="mb-2">
                  <h3
                    className="px-3 py-2 text-xs font-bold text-burgundy-600 uppercase tracking-wider border-b border-stone-100 cursor-pointer hover:text-burgundy-800 transition-colors"
                    onClick={() => navigate(coupleClubMenuItems.coupleBoard.href)}
                  >
                    {coupleClubMenuItems.coupleBoard.title}
                  </h3>
                  <div className="mt-1">
                    {coupleClubMenuItems.coupleBoard.items.map((item, index) => (
                      <DropdownMenuItem
                        key={item.label}
                        onClick={() => navigate(item.href)}
                        className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                        data-testid={`couple-club-item-${index}`}
                      >
                        {item.label}
                      </DropdownMenuItem>
                    ))}
                  </div>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* 멤버십 Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger
                className={`text-sm flex items-center gap-1 cursor-pointer outline-none transition-colors duration-300 ${
                  isScrolled 
                    ? "text-stone-700 hover:text-burgundy-500" 
                    : "text-white hover:text-gold-300"
                }`}
                data-testid="nav-membership-trigger"
              >
                멤버십
                <ChevronDown className="w-4 h-4 transition-transform duration-200" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="w-56 bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-2"
                data-testid="membership-dropdown-content"
              >
                {/* 회원정보보기 */}
                <DropdownMenuLabel 
                  className="px-4 py-2 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:bg-burgundy-50 rounded-lg"
                  onClick={() => goToMembers('/members')}
                  data-testid="membership-info-label"
                >
                  {membershipMenuItems.memberInfo.title}
                </DropdownMenuLabel>
                {membershipMenuItems.memberInfo.items.map((item, index) => (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => item.isRoute ? goToMembers(item.href) : scrollToSection(item.href)}
                    className="px-4 py-2 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                    data-testid={`membership-info-${index}`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
                
                <DropdownMenuSeparator className="bg-stone-200 my-2" />
                
                {/* 가입표 */}
                <DropdownMenuLabel 
                  className="px-4 py-2 text-xs text-burgundy-500 font-bold underline cursor-pointer hover:bg-burgundy-50 rounded-lg"
                  onClick={() => goToMembershipPage()}
                >
                  {membershipMenuItems.plans.title}
                </DropdownMenuLabel>
                {membershipMenuItems.plans.items.map((item, index) => (
                  <DropdownMenuItem
                    key={item.label}
                    onClick={() => goToMembershipPage(item.planId)}
                    className="px-4 py-2 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200"
                    data-testid={`membership-plan-${index}`}
                  >
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth Section */}
            {isAuthenticated ? (
              <div className="flex items-center gap-2">

                {/* Referral Gift Icon */}
                <ReferralPopover isScrolled={isScrolled} />

                {/* Notification Bell */}
                <NotificationDropdown />
                
                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    className={`relative flex items-center gap-2 cursor-pointer outline-none transition-colors duration-300 ${
                      isScrolled 
                        ? "text-stone-700 hover:text-burgundy-500" 
                        : "text-white hover:text-gold-300"
                    }`}
                    data-testid="nav-user-menu-trigger"
                  >
                    <div className="w-8 h-8 rounded-full bg-burgundy-100 flex items-center justify-center overflow-hidden">
                      {user?.profile_image ? (
                        <img src={user.profile_image} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-4 h-4 text-burgundy-500" />
                      )}
                    </div>
                    <ChevronDown className="w-4 h-4" />
                    {unreadCount > 0 && (
                      <span
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white shadow-sm tabular-nums"
                        data-testid="nav-unread-badge"
                      >
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </span>
                    )}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="w-48 bg-white/95 backdrop-blur-md border border-stone-200 shadow-xl rounded-xl p-2"
                    data-testid="user-dropdown-content"
                  >
                    {/* 포인트 잔액 표시 */}
                    <div 
                      className="px-4 py-3 mb-2 bg-stone-800 rounded-lg cursor-pointer hover:bg-stone-700 transition-colors"
                      onClick={() => navigate("/points")}
                    >
                      <p className="text-xs text-stone-400 mb-0.5">내 포인트</p>
                      <p className="text-lg font-bold text-white flex items-center gap-1.5">
                        <Coins className="w-4 h-4 text-amber-400" />
                        {(user?.points || 0).toLocaleString()}P
                      </p>
                    </div>
                    <DropdownMenuItem
                      onClick={goToProfile}
                      className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2"
                      data-testid="nav-profile"
                    >
                      <User className="w-4 h-4" />
                      내 프로필
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate("/credits")}
                      className="px-4 py-2.5 text-sm text-stone-700 hover:text-burgundy-500 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2"
                      data-testid="nav-credits"
                    >
                      <GraduationCap className="w-4 h-4" />
                      학점 관리
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowReferral(true)}
                      className="px-4 py-2.5 text-sm text-burgundy-700 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2 font-medium"
                      data-testid="nav-referral"
                    >
                      <Gift className="w-4 h-4 text-burgundy-600" />
                      친구에게 추천하기
                      <span className="ml-auto text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">+5,000P</span>
                    </DropdownMenuItem>
                    {isAdminUser && (
                      <>
                        <DropdownMenuSeparator className="bg-stone-200" />
                        <DropdownMenuItem
                          onClick={() => navigate("/admin")}
                          className="px-4 py-2.5 text-sm text-burgundy-600 hover:bg-burgundy-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2 font-medium"
                          data-testid="nav-admin"
                        >
                          <Settings className="w-4 h-4" />
                          관리자 대시보드
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator className="bg-stone-200" />
                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors duration-200 flex items-center gap-2"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-4 h-4" />
                      로그아웃
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <ReferralPopover isScrolled={isScrolled} />
                <button
                  onClick={openAuthModal}
                  className={`text-sm font-medium transition-colors duration-300 ${
                    isScrolled 
                      ? "text-burgundy-500 hover:text-burgundy-600" 
                      : "text-gold-300 hover:text-gold-200"
                  }`}
                  data-testid="nav-auth"
                >
                  로그인/회원가입
                </button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 transition-colors duration-300 ${
              isScrolled ? "text-burgundy-500" : "text-white"
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden absolute top-20 left-0 right-0 bg-white/95 backdrop-blur-md shadow-lg border-t border-stone-200 max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col py-4">
              {/* Mobile 커뮤니티 Dropdown */}
              <div className="px-6">
                <button
                  onClick={() => setIsMobileCommunityOpen(!isMobileCommunityOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-community-trigger"
                >
                  <span className="font-medium">커뮤니티</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileCommunityOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isMobileCommunityOpen && (
                  <div
                    className="pl-4 pb-2 space-y-1"
                    data-testid="mobile-community-submenu"
                  >
                    <button
                      onClick={() => goToCommunity()}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-semibold border-b border-stone-200 mb-2 hover:bg-burgundy-50"
                    >
                      중년들의 결로 모이는 따뜻한 SNS 커뮤니티
                    </button>
                    {communityMenuItems.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => goToCommunityItem(item)}
                        className="block w-full py-2 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-community-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    {/* 소그룹 채팅 */}
                    <div className="pt-3 mt-3 border-t border-stone-200">
                      <button
                        onClick={() => {
                          navigate("/chat/group");
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full py-2 text-left text-sm text-burgundy-600 font-semibold hover:text-burgundy-700 transition-colors duration-200 flex items-center gap-2"
                        data-testid="mobile-group-chat-landing"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                        소그룹 채팅방
                      </button>
                      {groupChatMenuItems.map((item) => (
                        <button
                          key={item.ageGroup}
                          onClick={() => {
                            if (!isAuthenticated) {
                              openAuthModal();
                              setIsMobileMenuOpen(false);
                              return;
                            }
                            navigate(`/chat/group/${item.ageGroup}`);
                            setIsMobileMenuOpen(false);
                          }}
                          className="block w-full py-2 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200 flex items-center gap-2 pl-4"
                        >
                          <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile 매칭 Dropdown */}
              <div className="px-6">
                <button
                  onClick={() => setIsMobileMatchingOpen(!isMobileMatchingOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-matching-trigger"
                >
                  <span className="font-medium">매칭</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileMatchingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isMobileMatchingOpen && (
                  <div
                    className="pl-4 pb-2 space-y-1"
                    data-testid="mobile-matching-submenu"
                  >
                    {matchingMenuItems.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          if (item.isRoute) {
                            navigate(item.href);
                            setIsMobileMenuOpen(false);
                          } else {
                            scrollToSection(item.href);
                          }
                        }}
                        className="block w-full py-2 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-matching-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile 이벤트 Dropdown */}
              <div className="px-6">
                <button
                  onClick={() => setIsMobileEventOpen(!isMobileEventOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-event-trigger"
                >
                  <span className="font-medium">이벤트</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileEventOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isMobileEventOpen && (
                  <div
                    className="pl-4 pb-2 space-y-1"
                    data-testid="mobile-event-submenu"
                  >
                    {/* 오프모임 → 전체보기 */}
                    <button
                      onClick={() => { navigate("/events"); setIsMobileMenuOpen(false); }}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-bold underline"
                      data-testid="mobile-event-offline-title"
                    >
                      {eventMenuItems.offlineMeeting.title}
                    </button>
                    {/* 매칭&디너 - 전체보기 바로 아래 */}
                    <button
                      onClick={() => { navigate(eventMenuItems.matchingDinner.titleHref || "/events"); setIsMobileMenuOpen(false); }}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-bold underline"
                      data-testid="mobile-event-matching-title"
                    >
                      {eventMenuItems.matchingDinner.title}
                    </button>
                    {eventMenuItems.offlineMeeting.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-event-offline-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    {/* 소모임 */}
                    <div className="py-2 text-xs text-burgundy-500 font-bold underline mt-2">
                      {eventMenuItems.smallGroup.title}
                    </div>
                    {eventMenuItems.smallGroup.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-event-small-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    {/* 파티 */}
                    <div className="py-2 text-xs text-burgundy-500 font-bold underline mt-2">
                      {eventMenuItems.party.title}
                    </div>
                    {eventMenuItems.party.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-event-party-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    {/* 캠프 */}
                    <div className="py-2 text-xs text-burgundy-500 font-bold underline mt-2">
                      {eventMenuItems.camp.title}
                    </div>
                    {eventMenuItems.camp.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-event-camp-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile 코칭&컨설팅 Dropdown */}
              <div className="px-6">
                <button
                  onClick={() => setIsMobileCoachingOpen(!isMobileCoachingOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-coaching-trigger"
                >
                  <span className="font-medium">코칭&컨설팅</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileCoachingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isMobileCoachingOpen && (
                  <div
                    className="pl-4 pb-2 space-y-1"
                    data-testid="mobile-coaching-submenu"
                  >
                    {/* 이미지 컨설팅 */}
                    <button
                      onClick={() => { navigate('/image-consulting'); setIsMobileMenuOpen(false); setIsMobileCoachingOpen(false); }}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-bold underline hover:bg-burgundy-50 rounded"
                    >
                      {coachingMenuItems.imageConsulting.title}
                    </button>
                    {coachingMenuItems.imageConsulting.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          if (item.isRoute) {
                            navigate(item.href);
                            setIsMobileMenuOpen(false);
                            setIsMobileCoachingOpen(false);
                          } else {
                            scrollToSection(item.href);
                          }
                        }}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-coaching-image-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    {/* 매너클라스 */}
                    <button
                      onClick={() => { navigate('/manner-class'); setIsMobileMenuOpen(false); setIsMobileCoachingOpen(false); }}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-bold underline hover:bg-burgundy-50 rounded mt-2"
                    >
                      {coachingMenuItems.mannerClass.title}
                    </button>
                    {coachingMenuItems.mannerClass.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          if (item.isRoute) {
                            navigate(item.href);
                            setIsMobileMenuOpen(false);
                            setIsMobileCoachingOpen(false);
                          } else {
                            scrollToSection(item.href);
                          }
                        }}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-coaching-manner-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                    
                    {/* 연애사관학교 */}
                    <button
                      onClick={() => { navigate('/love-academy'); setIsMobileMenuOpen(false); setIsMobileCoachingOpen(false); }}
                      className="block w-full py-2 text-left text-xs text-burgundy-500 font-bold underline hover:bg-burgundy-50 rounded mt-2"
                    >
                      {coachingMenuItems.loveAcademy.title}
                    </button>
                    {coachingMenuItems.loveAcademy.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          if (item.isRoute) {
                            navigate(item.href);
                            setIsMobileMenuOpen(false);
                            setIsMobileCoachingOpen(false);
                          } else {
                            scrollToSection(item.href);
                          }
                        }}
                        className="block w-full py-1.5 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-coaching-academy-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile 리즈상담 Dropdown */}
              <div className="px-6">
                <button
                  onClick={() => setIsMobileCounselingOpen(!isMobileCounselingOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-counseling-trigger"
                >
                  <span className="font-medium">리즈상담</span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform duration-200 ${
                      isMobileCounselingOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isMobileCounselingOpen && (
                  <div
                    className="pl-4 pb-2 space-y-1"
                    data-testid="mobile-counseling-submenu"
                  >
                    {counselingMenuItems.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => scrollToSection(item.href)}
                        className="block w-full py-2 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-counseling-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile 리즈커플즈 */}
              <div className="px-6">
                <button
                  onClick={() => setMobileCoupleClubOpen(!mobileCoupleClubOpen)}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-nav-couple-club"
                >
                  <span className="font-medium">리즈커플즈</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileCoupleClubOpen ? "rotate-180" : ""}`} />
                </button>
                {mobileCoupleClubOpen && (
                  <div className="pl-4 pb-2 space-y-1" data-testid="mobile-couple-club-submenu">
                    <p className="py-2 text-xs font-bold text-burgundy-600 uppercase tracking-wider">
                      {coupleClubMenuItems.coupleBoard.title}
                    </p>
                    {coupleClubMenuItems.coupleBoard.items.map((item, index) => (
                      <button
                        key={item.label}
                        onClick={() => {
                          navigate(item.href);
                          setIsMobileMenuOpen(false);
                        }}
                        className="block w-full py-2 text-left text-sm text-stone-600 hover:text-burgundy-500 transition-colors duration-200"
                        data-testid={`mobile-couple-club-item-${index}`}
                      >
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Mobile 멤버십 — 가격/결제 페이지로 바로 이동 */}
              <div className="px-6">
                <button
                  onClick={() => goToMembershipPage('__pricing__')}
                  className="w-full flex items-center justify-between py-3 text-left text-stone-700 hover:text-burgundy-500 transition-colors duration-200"
                  data-testid="mobile-membership-trigger"
                >
                  <span className="font-medium">멤버십</span>
                  <ChevronRight className="w-4 h-4 text-stone-400" />
                </button>
              </div>

              {/* Mobile Auth Section */}
              {isAuthenticated ? (
                <>
                  <button
                    onClick={goToProfile}
                    className="px-6 py-3 text-left text-stone-700 hover:bg-stone-50 hover:text-burgundy-500 transition-colors duration-200 flex items-center gap-2"
                    data-testid="mobile-nav-profile"
                  >
                    <User className="w-4 h-4" />
                    내 프로필 ({user?.username})
                  </button>
                  <button
                    onClick={() => { setShowReferral(true); setIsMobileMenuOpen(false); }}
                    className="px-6 py-3 text-left text-burgundy-700 hover:bg-burgundy-50 transition-colors duration-200 flex items-center gap-2 font-medium"
                    data-testid="mobile-nav-referral"
                  >
                    <Gift className="w-4 h-4 text-burgundy-600" />
                    친구에게 추천하기
                    <span className="ml-auto text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">+5,000P</span>
                  </button>
                  {isAdminUser && (
                    <>
                      <div className="px-6 py-2 text-xs font-bold text-burgundy-600 bg-burgundy-50">
                        관리자 메뉴
                      </div>
                      <button
                        onClick={() => {
                          navigate("/admin");
                          setIsMobileMenuOpen(false);
                        }}
                        className="px-6 py-3 text-left text-burgundy-600 hover:bg-burgundy-50 transition-colors duration-200 flex items-center gap-2"
                        data-testid="mobile-nav-admin"
                      >
                        <Settings className="w-4 h-4" />
                        관리자 대시보드
                      </button>
                    </>
                  )}
                  <button
                    onClick={handleLogout}
                    className="px-6 py-3 text-left text-red-600 hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                    data-testid="mobile-nav-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    로그아웃
                  </button>
                </>
              ) : (
                <button
                  onClick={openAuthModal}
                  className="px-6 py-3 text-left text-burgundy-500 font-medium hover:bg-burgundy-50 transition-colors duration-200"
                  data-testid="mobile-nav-auth"
                >
                  로그인/회원가입
                </button>
              )}
              <div className="px-6 pt-4 pb-2">
                <Button
                  onClick={() => scrollToSection("#membership")}
                  className="btn-primary w-full text-sm"
                  data-testid="mobile-cta-button"
                >
                  시작하기
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Curriculum Modal */}
      <CurriculumModal 
        isOpen={isCurriculumModalOpen} 
        onClose={() => setIsCurriculumModalOpen(false)} 
      />

      {/* Membership Modal */}
      <MembershipModal 
        isOpen={isMembershipModalOpen} 
        onClose={() => setIsMembershipModalOpen(false)} 
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authModalInitialMode}
      />

      {/* Membership Guard Modal (회원목록 정회원 전용 가드) */}
      <MembershipGuardModal
        open={!!membersGuard}
        reason={membersGuard || "upgrade"}
        onClose={() => setMembersGuard(null)}
      />
      <ReferralModal open={showReferral} onClose={() => setShowReferral(false)} user={user} />
    </nav>
    </div>
  );
};
